import AlertMinimalIcon from '@assets/icons/alert-minimal.svg';
import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@/components/button';
import { ChatStatus } from '@/enums';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_CURRENCY_CODE,
  formatCurrency,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { getAddedVariantPackEventPayloads } from '@/utils/ads';
import {
  getHashFromModifierUUIDs,
  getMinAndMaxQuantity,
  getPackModifierMaps,
} from '@/contexts/item-context-utils';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { MultiStyleText } from '@/components/multi-style-text';
import { QuantitySelector } from '@/components/quantity-selector';
import { RadioButtonGroup } from '@/components/radio-button-group';
import { setNextLayoutAnimation } from '@/utils/layout-animation';
import { Text, View } from 'react-native';
import { UpdateQuantityCallbackType } from '@/contexts/new-order-actions-context-types';
import { useItemVisibleQuantity } from '@/stores/new-order-quantity-store';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

interface Props {
  closeSelector: () => void;
  metadata: ItemListOptionMetadata;
  onNavigateToBecomeCustomer?: () => void;
  scrollTo: (y: number) => void;
  updateQuantity: UpdateQuantityCallbackType;
}

export const ItemDetailSelector: React.FC<Props> = ({
  closeSelector,
  metadata,
  onNavigateToBecomeCustomer,
  scrollTo,
  updateQuantity,
}) => {
  const { essentialVariantPackUUIDs, getVariantPackInfo } =
    useVariantPackInfoContext();
  const {
    selectedRestaurantRelationship: { chatStatus },
  } = useUserContext();
  const isProspect = chatStatus === ChatStatus.PROSPECT;
  const [styles] = useStyles();

  const bottomOfSelector = useRef<number | null>(null);
  const { packModifierDisplayMap, packModifierHashMap } = useMemo(
    () => getPackModifierMaps(metadata),
    [metadata],
  );
  const hasSinglePack = useMemo<boolean>(
    () => Object.keys(packModifierDisplayMap).length === 1,
    [packModifierDisplayMap],
  );

  const [quantity, setQuantity] = useState<number>(1);
  const [initialQuantity, setInitialQuantity] = useState<number>(1);
  const [selectedPackString, setSelectedPackString] = useState<string>(
    hasSinglePack ? Object.keys(packModifierDisplayMap)[0] : '',
  );
  const [selectedModifierUUIDs, setSelectedModifierUUIDs] = useState<string[]>(
    [],
  );

  const modifierGroupCount = useMemo<number>(
    () =>
      selectedPackString
        ? packModifierDisplayMap[selectedPackString].length
        : Object.values(packModifierDisplayMap)[0].length,
    [packModifierDisplayMap, selectedPackString],
  );

  const selectedVariantPackUUID = useMemo<string | null>(
    () =>
      selectedPackString && selectedModifierUUIDs.length === modifierGroupCount
        ? (packModifierHashMap[selectedPackString][
            getHashFromModifierUUIDs(selectedModifierUUIDs)
          ]?.variantPackUUID ?? null)
        : null,
    [
      selectedPackString,
      selectedModifierUUIDs,
      modifierGroupCount,
      packModifierHashMap,
    ],
  );

  const visibleQuantity = useItemVisibleQuantity(selectedVariantPackUUID);

  const isEssential = selectedVariantPackUUID
    ? essentialVariantPackUUIDs.has(selectedVariantPackUUID)
    : false;

  // Only check constraint for selected variant pack. Falling back to the
  // metadata prop's constraints could be pointing to the wrong values
  const { maximumQuantity, minimumQuantity } = getMinAndMaxQuantity(
    selectedVariantPackUUID
      ? getVariantPackInfo(selectedVariantPackUUID)
      : null,
  );

  // For components that should only be rendered for specific VPs (leaf node mods)
  const getSelectedVPComponents = useCallback(
    (lastGroupModifierUUID: string) => {
      // Construct the full list of modifierUUIDs for VP with lastGroupModifierUUID.
      // Since the bottom most mod group could already be selected,
      // we need to check and remove that selection if it exists so the list is valid.
      const modifierUUIDs = [
        ...(selectedModifierUUIDs.length === modifierGroupCount
          ? selectedModifierUUIDs.slice(0, -1)
          : selectedModifierUUIDs),
        lastGroupModifierUUID,
      ];
      const UUIDHash = getHashFromModifierUUIDs(modifierUUIDs);

      const variantPackInfo = packModifierHashMap[selectedPackString][UUIDHash];
      if (!variantPackInfo) {
        return { rightElement: [] };
      }
      const {
        baseUnit,
        currencyCode,
        externalID,
        promotionPricePerUnitInMicros,
        unitCount,
        unitPriceInMicros,
      } = variantPackInfo;
      const resolvedCurrencyCode = currencyCode || DEFAULT_CURRENCY_CODE;
      if (!unitPriceInMicros) {
        return { rightElement: [], subLabel: externalID };
      }

      const discountedUnitPrice =
        typeof promotionPricePerUnitInMicros === 'number' &&
        roundMicrosToNearestWholeCent(promotionPricePerUnitInMicros) !==
          roundMicrosToNearestWholeCent(unitPriceInMicros)
          ? promotionPricePerUnitInMicros
          : null;
      const unitPrice = formatCurrency(unitPriceInMicros, resolvedCurrencyCode);

      const rightElement = [
        baseUnit ? (
          <MultiStyleText
            defaultStyle={[TYPOGRAPHY.listSmall, styles.perUnitPriceText]}
            emphasisStyle={[
              TYPOGRAPHY.listSmall,
              styles.perUnitPriceText,
              styles.strikethroughText,
            ]}
            key={`${UUIDHash}-unit-price`}
          >
            {discountedUnitPrice !== null
              ? i18n.t('per_unit_promo_price', {
                  previousPrice: unitPrice,
                  promoPrice: formatCurrency(
                    discountedUnitPrice,
                    resolvedCurrencyCode,
                  ),
                  unit: baseUnit,
                })
              : i18n.t('per_unit_price', { price: unitPrice, unit: baseUnit })}
          </MultiStyleText>
        ) : null,
        discountedUnitPrice !== null ? (
          <Text
            key={`${UUIDHash}-promotion-price`}
            style={[TYPOGRAPHY.pSemiBold, styles.priceText]}
          >
            {formatCurrency(
              unitCount * discountedUnitPrice,
              resolvedCurrencyCode,
            )}
          </Text>
        ) : null,
        <Text
          key={`${UUIDHash}-price`}
          style={[
            TYPOGRAPHY.pSemiBold,
            styles.priceText,
            discountedUnitPrice !== null ? styles.strikethroughText : null,
          ]}
        >
          {formatCurrency(unitCount * unitPriceInMicros, resolvedCurrencyCode)}
        </Text>,
      ].filter(Boolean);

      return { rightElement, subLabel: externalID };
    },
    [
      modifierGroupCount,
      packModifierHashMap,
      selectedModifierUUIDs,
      selectedPackString,
      styles,
    ],
  );

  const modifierGroupComponents = useMemo<React.ReactNode[]>(() => {
    return selectedPackString
      ? packModifierDisplayMap[selectedPackString].map((group, groupIdx) => {
          const { groupName, groupUUID, options } = group;
          const isLastGroup = groupIdx === modifierGroupCount - 1;
          // Only render up to next available modifier group
          return groupIdx <= selectedModifierUUIDs.length ? (
            <RadioButtonGroup
              key={groupUUID}
              onChange={(value: string) => {
                setSelectedModifierUUIDs((modifierUUIDs) => {
                  const modifiers = [...modifierUUIDs];
                  modifiers[groupIdx] = value;
                  return modifiers;
                });
                Analytics.track('item_selector_modifier_select.press', {
                  modifierGroupUUID: groupUUID,
                  modifierOptionUUID: value,
                });
                setNextLayoutAnimation();
                if (typeof bottomOfSelector.current === 'number') {
                  scrollTo(bottomOfSelector.current);
                }
              }}
              options={options.map((opt) => ({
                ...(isLastGroup
                  ? getSelectedVPComponents(opt.modifierUUID)
                  : {}),
                label: opt.modifierName,
                value: opt.modifierUUID,
              }))}
              selectedValue={selectedModifierUUIDs[groupIdx] ?? ''}
              subTextStyle={styles.packSubText}
              testID={`item-selector-${groupName.toLowerCase()}-group`}
              title={i18n.t('select_modifier_group', {
                modifierGroupName: groupName,
              })}
            />
          ) : null;
        })
      : [];
  }, [
    getSelectedVPComponents,
    modifierGroupCount,
    packModifierDisplayMap,
    scrollTo,
    selectedModifierUUIDs,
    selectedPackString,
    styles,
  ]);

  useEffect(() => {
    setInitialQuantity(visibleQuantity);
    setQuantity(visibleQuantity || minimumQuantity || 1);
  }, [minimumQuantity, visibleQuantity]);

  return (
    <View
      onLayout={(e) => {
        const { y, height } = e.nativeEvent.layout;
        bottomOfSelector.current = y + height;
      }}
      style={styles.base}
    >
      {hasSinglePack ? (
        <View style={styles.singlePack}>
          <Text style={[TYPOGRAPHY.small, styles.singlePackText]}>
            {selectedPackString}
          </Text>
        </View>
      ) : (
        <RadioButtonGroup
          onChange={(value: string) => {
            setSelectedPackString(value);
            Analytics.track('item_selector_pack_select.press', {
              packString: value,
            });
            setSelectedModifierUUIDs([]);
            setNextLayoutAnimation();
          }}
          options={Object.keys(packModifierDisplayMap).map((packString) => ({
            label: packString,
            value: packString,
          }))}
          selectedValue={selectedPackString}
          testID="item-selector-pack-group"
          textStyle={styles.packText}
          title={i18n.t('select_pack_unit')}
        />
      )}

      {modifierGroupComponents}
      {selectedVariantPackUUID ? (
        <View style={styles.bottomContainer}>
          {!isProspect ? (
            <View style={styles.quantityContainer}>
              <QuantitySelector
                additionalIcon={isEssential ? AlertMinimalIcon : null}
                canDecrementToZero={false}
                isFocused={false}
                maxQuantity={maximumQuantity}
                minQuantity={minimumQuantity}
                onChange={(_, quant) => setQuantity(quant)}
                onNavigateToBecomeCustomer={onNavigateToBecomeCustomer}
                quantity={quantity}
              />
            </View>
          ) : null}
          <Button
            containerStyle={styles.buttonContainer}
            onPress={() => {
              updateQuantity(
                {
                  eventPayloads: getAddedVariantPackEventPayloads(metadata),
                  newQuantity: quantity,
                  previousQuantity: initialQuantity,
                  variantPackDetails: {
                    itemDisplayName: metadata.displayName,
                    metadata: metadata.variantPackMetadata,
                  },
                  variantPackUUID: selectedVariantPackUUID,
                },
                {
                  source: 'item_detail_selector',
                },
              );
              closeSelector();
              if (typeof bottomOfSelector.current === 'number') {
                scrollTo(bottomOfSelector.current);
              }
            }}
            trackingData={{
              name: 'item_selector_quantity_update',
              properties: {
                quantity,
                variantPackUUID: selectedVariantPackUUID,
              },
            }}
          >
            {i18n.t('add_to_order')}
          </Button>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    marginTop: 16,
  },
  bottomContainer: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
  },
  buttonContainer: {
    marginBottom: 16,
  },
  packSubText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
    marginLeft: 8,
    maxWidth: 100,
  },
  packText: {
    textTransform: 'uppercase',
  },
  perUnitPriceText: {
    color: theme.colors.inactiveText,
    marginRight: 8,
    textTransform: 'lowercase',
  },
  priceText: {
    fontSize: 14,
    lineHeight: 18,
  },
  quantityContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 16,
  },
  singlePack: {
    marginBottom: 16,
  },
  singlePackText: {
    textTransform: 'uppercase',
  },
  strikethroughText: {
    textDecorationLine: 'line-through',
  },
}));
